import {
  accents,
  templates,
  type Accent,
  type Corners,
  type FontChoice,
  type TemplateId,
} from "./templates";

export const designPresets = [
  { id: "original", name: "Original", font: "Pretendard Variable" },
  { id: "editorial", name: "Editorial", font: "SUIT Variable" },
  { id: "minimal", name: "Minimal", font: "Pretendard Variable" },
] as const;

export type DesignPreset = (typeof designPresets)[number]["id"];
export type ThemeMode = "light" | "dark" | "system";
export type Density = "default" | "comfortable";
export type Preferences = {
  mode: ThemeMode;
  density: Density;
  design: DesignPreset;
  template: TemplateId;
  accent: Accent;
  corners: Corners;
  font: FontChoice;
};
export const appearanceKey = "studio.appearance.v1";
export const defaultPreferences: Preferences = {
  mode: "system",
  density: "default",
  design: "original",
  template: "classic",
  accent: "preset",
  corners: "preset",
  font: "preset",
};

export function isDesignPreset(value: unknown): value is DesignPreset {
  return designPresets.some((preset) => preset.id === value);
}

export function parsePreferences(raw: string): Preferences {
  try {
    const value = JSON.parse(raw);
    return {
      mode: ["light", "dark", "system"].includes(value?.mode)
        ? value.mode
        : "system",
      density: value?.density === "comfortable" ? "comfortable" : "default",
      design: isDesignPreset(value?.design) ? value.design : "original",
      template: templates.some((item) => item.id === value?.template)
        ? value.template
        : "classic",
      accent: accents.some((item) => item.id === value?.accent)
        ? value.accent
        : "preset",
      corners: ["preset", "square", "soft", "round"].includes(value?.corners)
        ? value.corners
        : "preset",
      font: ["preset", "pretendard", "suit", "system"].includes(value?.font)
        ? value.font
        : "preset",
    };
  } catch {
    return defaultPreferences;
  }
}

// Apply persisted values before first paint; v1 preferences without design keep Original.
const preferenceOptions = {
  mode: ["light", "dark", "system"],
  density: ["default", "comfortable"],
  design: designPresets.map((item) => item.id),
  template: templates.map((item) => item.id),
  accent: accents.map((item) => item.id),
  corners: ["preset", "square", "soft", "round"],
  font: ["preset", "pretendard", "suit", "system"],
};
export const appearanceInitScript = `try{var p=JSON.parse(localStorage.getItem('${appearanceKey}')||'{}')||{},d=${JSON.stringify(defaultPreferences)},o=${JSON.stringify(preferenceOptions)},r=document.documentElement;Object.keys(d).forEach(function(k){if(o[k].indexOf(p[k])<0)p[k]=d[k]});var m=p.mode==='system'?(matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light'):p.mode;r.dataset.theme=m;r.style.colorScheme=m;['density','design','template','accent','corners','font'].forEach(function(k){r.dataset[k]=p[k]})}catch(e){}`;
